import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarRange, Siren, Users, ShieldAlert } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Pill } from "@/components/Pill";
import { NameWithAvatar } from "@/components/Avatar";
import { StatCard } from "@/components/dashboard/StatCards";

const iso = (d) => d.toISOString().slice(0, 10);

function Report() {
  const today = new Date();
  const [range, setRange] = useState({ start: iso(new Date(today.getTime() - 6 * 86400000)), end: iso(today), user_id: "" });
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: () => api.get("/team").then((r) => r.data) });
  const params = { start: range.start, end: range.end, ...(range.user_id ? { user_id: range.user_id } : {}) };
  const { data, error } = useQuery({ queryKey: ["admin-report", params], queryFn: () => api.get("/admin/report", { params }).then((r) => r.data), enabled: Boolean(range.start && range.end) });
  const max = Math.max(1, ...(data?.by_day || []).map((d) => Math.max(d.created, d.resolved)));
  return (
    <section className="space-y-4 rounded-2xl border-2 border-black bg-white p-4" data-testid="date-report">
      <div className="flex flex-wrap items-end gap-2">
        <h3 className="mr-auto flex items-center gap-2 font-display text-lg font-bold"><CalendarRange className="h-5 w-5" /> Date-range report</h3>
        <input type="date" className="field-input !w-auto !py-2 !text-sm" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="report-start-date" />
        <input type="date" className="field-input !w-auto !py-2 !text-sm" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="report-end-date" />
        <Select value={range.user_id || "team"} onValueChange={(v) => setRange({ ...range, user_id: v === "team" ? "" : v })}>
          <SelectTrigger className="h-10 w-[200px] border-2 border-black bg-white text-sm font-semibold" data-testid="report-user-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="team">Whole team</SelectItem>
            {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {error && <p className="text-sm font-semibold text-[#B91C1C]" data-testid="report-error">{error.response?.data?.detail || "Could not load report"}</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Generated" value={data.generated} tone="yellow" testId="report-generated" />
            <StatCard label="Resolved" value={data.resolved} tone="black" testId="report-resolved" />
            <StatCard label="Still open (of generated)" value={data.still_open} tone="red" testId="report-still-open" />
            <StatCard label="Unassigned (of generated)" value={data.unassigned_generated} testId="report-unassigned" />
          </div>
          <div className="flex h-40 items-end gap-1 overflow-x-auto rounded-xl border-2 border-zinc-200 p-3" data-testid="report-chart">
            {data.by_day.map((d) => (
              <div key={d.date} className="flex min-w-[26px] flex-1 flex-col items-center gap-0.5" title={`${d.date}: ${d.created} generated, ${d.resolved} resolved`}>
                <div className="flex w-full items-end justify-center gap-0.5" style={{ height: "110px" }}>
                  <div className="w-2/5 rounded-t border border-black bg-[#FACC15]" style={{ height: `${(d.created / max) * 100}%` }} />
                  <div className="w-2/5 rounded-t border border-black bg-black" style={{ height: `${(d.resolved / max) * 100}%` }} />
                </div>
                <span className="text-[9px] text-zinc-500">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500"><span className="inline-block h-2.5 w-2.5 border border-black bg-[#FACC15]" /> generated · <span className="inline-block h-2.5 w-2.5 border border-black bg-black" /> resolved</p>
          {!range.user_id && data.per_executive.length > 0 && (
            <table className="w-full text-left text-sm" data-testid="report-per-executive">
              <thead className="text-[10px] uppercase tracking-wider text-zinc-500"><tr><th className="py-2">Executive</th><th className="py-2 text-right">Generated (assigned)</th><th className="py-2 text-right">Resolved</th><th className="py-2 text-right">Still open</th><th className="py-2 text-right"></th></tr></thead>
              <tbody className="divide-y divide-zinc-200">
                {data.per_executive.map((r) => (
                  <tr key={r.id} data-testid={`report-exec-${r.id}`}>
                    <td className="py-2 font-semibold"><NameWithAvatar id={r.id} name={r.name} size={22} /></td><td className="py-2 text-right font-mono">{r.generated}</td><td className="py-2 text-right font-mono font-bold">{r.resolved}</td><td className="py-2 text-right font-mono">{r.still_open}</td>
                    <td className="py-2 text-right"><button type="button" className="text-xs font-bold underline" onClick={() => setRange({ ...range, user_id: r.id })} data-testid={`report-drill-${r.id}`}>Drill in →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

export function Overview({ onOpenTicket }) {
  const { data } = useQuery({ queryKey: ["admin-overview"], queryFn: () => api.get("/admin/overview").then((r) => r.data), refetchInterval: 60000 });
  if (!data) return <p className="text-zinc-600" data-testid="overview-loading">Loading overview…</p>;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Leads today" value={data.total_today} tone="yellow" testId="ov-total-today" />
        <StatCard label="Assigned today" value={data.assigned_today} testId="ov-assigned-today" />
        <StatCard label="Unassigned today" value={data.unassigned_today} tone="red" testId="ov-unassigned-today" />
        <StatCard label="Resolved today" value={data.resolved_today} tone="black" testId="ov-resolved-today" />
        <StatCard label="Unresolved (all)" value={data.unresolved_total} testId="ov-unresolved-total" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="overflow-x-auto rounded-2xl border-2 border-black bg-white">
          <h3 className="flex items-center gap-2 border-b-2 border-black bg-[#FACC15] px-4 py-3 font-display text-base font-bold"><Users className="h-4 w-4" /> Per executive</h3>
          <table className="w-full text-left text-sm" data-testid="per-executive-table">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500"><tr><th className="px-4 py-2">Name</th><th className="px-2 py-2 text-right">Assigned</th><th className="px-2 py-2 text-right">Resolved</th><th className="px-4 py-2 text-right">Unresolved</th></tr></thead>
            <tbody className="divide-y divide-zinc-200">
              {data.per_executive.map((r) => (
                <tr key={r.id} className={r.active ? "" : "opacity-50"} data-testid={`exec-row-${r.id}`}>
                  <td className="px-4 py-2 font-semibold"><NameWithAvatar id={r.id} name={r.name} size={24} /> <span className="text-[10px] uppercase text-zinc-500">{r.role}{r.active ? "" : " · inactive"}</span></td>
                  <td className="px-2 py-2 text-right font-mono">{r.assigned}</td>
                  <td className="px-2 py-2 text-right font-mono text-[#15803D]">{r.resolved}</td>
                  <td className="px-4 py-2 text-right font-mono font-bold">{r.unresolved}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="overflow-x-auto rounded-2xl border-2 border-black bg-white">
          <h3 className="flex items-center gap-2 border-b-2 border-black bg-[#DC2626] px-4 py-3 font-display text-base font-bold text-white"><Siren className="h-4 w-4" /> Needs attention now <span className="ml-auto rounded-full bg-white px-2 text-xs text-black" data-testid="urgent-count">{data.urgent_count}</span></h3>
          <ul className="max-h-[420px] divide-y divide-zinc-200 overflow-y-auto" data-testid="urgent-list">
            {data.urgent.map((t) => (
              <li key={t.id} className="flex cursor-pointer flex-wrap items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#FEF9C3]" onClick={() => onOpenTicket(t)} data-testid={`urgent-${t.ticket_id}`}>
                <span className="font-mono font-bold">{t.ticket_id}</span>
                <Pill kind="priority" value={t.priority} /><Pill kind="level" value={t.escalation_level} /><Pill kind="qrc" value={t.qrc_type} />
                <span className="truncate text-zinc-600">{t.name} · {t.query}</span>
                <span className="ml-auto text-xs text-zinc-500">{t.assigned_to || "Unassigned"} · {t.elapsed_working_hours}h</span>
              </li>
            ))}
            {!data.urgent.length && <li className="px-4 py-8 text-center text-zinc-500">Nothing urgent. Nice.</li>}
          </ul>
        </section>
      </div>

      <Report />

      <section className="rounded-2xl border-2 border-black bg-white p-4" data-testid="audit-log">
        <h3 className="flex items-center gap-2 font-display text-base font-bold"><ShieldAlert className="h-4 w-4" /> Audit log</h3>
        {!data.audit_log.length ? <p className="mt-2 text-sm text-zinc-500">No deletions recorded.</p> : (
          <ul className="mt-2 divide-y divide-zinc-200 text-sm">
            {data.audit_log.map((a) => (
              <li key={a.id} className="flex flex-wrap gap-2 py-2" data-testid={`audit-${a.ticket_id}`}>
                <span className="font-mono font-bold">{a.ticket_id}</span><span>{a.action.replace("_", " ")}</span><span className="text-zinc-500">by {a.by} · {fmtDate(a.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
